/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit'
import multiPoolConfig from 'config/constants/multiPool'
import { DEFAULT_TOKEN_DECIMAL } from 'config'
import { MultiPoolState } from 'state/types'
import { getPoolApr } from 'utils/apr'
import { getBalanceNumber } from 'utils/formatBalance'
import { getAddress, getDmdAddress } from 'utils/addressHelpers'
import { BIG_ZERO } from 'utils/bigNumber'
import { fetchMultiPoolEarningTokensInfo, fetchMultiPoolTotalSupply } from './fetchMultiPool'
import { fetchMultiPoolAllowance, fetchUserStakedBalance, fetchUserEarnedMultiPool } from './fetchMultiPoolUser'

const initialState: MultiPoolState = {
  data: multiPoolConfig,
  userDataLoaded: false,
}

// Thunks
export const fetchMultiPoolPublicDataAsync = () => async (dispatch, getState) => {
  const prices = getState().farms.prices

  let earningTokensInfo = []
  let totalStaked = BIG_ZERO
  const setEarningTokensInfo = async () => {
    earningTokensInfo = await fetchMultiPoolEarningTokensInfo()
  }
  const setTotalStaked = async () => {
    totalStaked = await fetchMultiPoolTotalSupply()
  }
  await Promise.all([setEarningTokensInfo(), setTotalStaked()])

  const stakingTokenPrice = prices[getDmdAddress().toLowerCase()]
  const tvlUsdBG = totalStaked.multipliedBy(stakingTokenPrice).div(DEFAULT_TOKEN_DECIMAL)
  // const tvlUsd = tvlUsdBG.isNaN() || !tvlUsdBG.isFinite() ? null : tvlUsdBG.toNumber()

  const earningTokensApr = earningTokensInfo.map((item, index) => {
    const earningTokenPrice = prices[getAddress(multiPoolConfig.earningTokens[index].address).toLocaleLowerCase()]
    return !item.isFinished
      ? getPoolApr(
          stakingTokenPrice,
          earningTokenPrice,
          getBalanceNumber(totalStaked),
          getBalanceNumber(item.rewardRate, multiPoolConfig.earningTokens[index].decimals),
        ) || 0
      : 0
  })

  dispatch(
    setMultiPoolPublicData({
      earningTokensApr,
      tvlUsd: tvlUsdBG,
    }),
  )
}

export const fetchMultiPoolUserDataAsync =
  (account: string): any =>
  async (dispatch, getState) => {
    const prices = getState().farms.prices

    let allowance = BIG_ZERO
    let stakedBalance = BIG_ZERO
    let earns = []
    const setAllowance = async () => {
      allowance = await fetchMultiPoolAllowance(account)
    }
    const setStakedBalance = async () => {
      stakedBalance = await fetchUserStakedBalance(account)
    }
    const setEarns = async () => {
      earns = await fetchUserEarnedMultiPool(account)
    }
    await Promise.all([setAllowance(), setStakedBalance(), setEarns()])
    earns = earns.map((item, index) => {
      const earningTokenPrice =
        prices[getAddress(multiPoolConfig.earningTokens[index].address).toLocaleLowerCase()] || 0
      const tokenValue = getBalanceNumber(item, multiPoolConfig.earningTokens[index].decimals)
      return {
        tokenValue,
        usdVaule: tokenValue * earningTokenPrice,
      }
    })

    dispatch(
      setPoolsUserData({
        allowance,
        stakedBalance,
        earns,
      }),
    )
  }

export const MultiPoolSlice = createSlice({
  name: 'MultiPool',
  initialState,
  reducers: {
    setMultiPoolPublicData: (state, action) => {
      const publicData = action.payload
      state.data = {
        ...state.data,
        ...publicData,
      }
    },
    setPoolsUserData: (state, action) => {
      const userData = action.payload
      state.data = {
        ...state.data,
        userData,
      }
      state.userDataLoaded = true
    },
  },
})

// Actions
export const { setMultiPoolPublicData, setPoolsUserData } = MultiPoolSlice.actions

export default MultiPoolSlice.reducer
